import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import type { Op } from 'quill/core';
import React, { useEffect, useState } from 'react';
import Toast from '../../components/Toast';
import { CREATION_ID } from '../../editor/editorSlice';
import RESOLVE_THREAD_MUTATION from '../../graphql/resolveThread';
import UIActions from '../../ui/UIActions';
import Comment from './Comment';
import CommentCreate from './CommentCreate';
import FoldedComments from './FoldedComments';
import handleUnresolveThreadError from './handleUnresolveThreadError';
import type { ResizeHandler } from '../MagneticWall';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { Comment_CommentFragment } from '../../graphql/generated/graphql';
import { usePostLayoutApi } from '../PostLayoutProvider';

const COMMENT_LIMIT = 2;
const EXPANDED_COMMENT_LIMIT = 6;

const POST_FRAGMENT = gql(`
  fragment CommentThreadFull_post on RootPostFields {
    id
    ...Comment_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment CommentThreadFull_thread on PublicThreadFields {
    id
    comments {
      id
      ...Comment_comment
    }
    ...CommentCreate_thread
  }
`);

export interface CommentThreadFullProps {
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  isExpanded?: boolean;
  hovered?: boolean;
  initialShowingMore?: boolean;
  onCreated?: () => void;
  onResize?: ResizeHandler;
  unsavedInputValue?: Op[];
  onInputChange?: (threadId: string, content: Op[]) => void;
  unresolveThread: (threadId: string) => Promise<unknown>;
}

const CommentThreadFull = ({
  thread,
  post,
  isExpanded = false,
  hovered,
  initialShowingMore = false,
  onCreated,
  onResize = () => {},
  unsavedInputValue = [],
  onInputChange,
  unresolveThread,
}: CommentThreadFullProps) => {
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [isHoveredWithDelay, setIsHoveredWithDelay] = useState(hovered);
  useEffect(() => {
    if (!hovered) {
      setIsHoveredWithDelay(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setIsHoveredWithDelay(true);
    }, 200);

    return () => clearTimeout(timer);
  }, [hovered]);

  const viewState = (() => {
    if (isExpanded) return 'expanded' as const;
    if (isHoveredWithDelay) return 'hovered' as const;
    return 'collapsed' as const;
  })();

  useEffect(() => {
    onResize();

    if (viewState === 'collapsed') {
      setEditingCommentId(null);
    }
  }, [viewState, onResize]);

  const dispatch = useAppDispatch();

  const [resolveThreadMutation] = useMutation(RESOLVE_THREAD_MUTATION);

  const resolveThread = () =>
    resolveThreadMutation({ variables: { id: thread.id } });

  const { triggerMagneticWallCollapseAction } = usePostLayoutApi();
  const handleResolve = () => {
    resolveThread().then(() => {
      triggerMagneticWallCollapseAction();

      dispatch(
        UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
          <Toast
            level="success"
            onDismiss={toastProps.onDismiss}
            message="Comment resolved"
            secondaryText="Undo"
            onSecondaryClick={() => {
              toastProps.onDismiss();
              unresolveThread(thread.id).catch((e) =>
                handleUnresolveThreadError(e, dispatch),
              );
            }}
          />
        )),
      );
    });
  };

  const { id: threadId, comments } = thread;
  const isCreating = threadId === CREATION_ID;

  return (
    <div
      className={classNames('comment-thread-full', {
        expanded: isExpanded,
      })}
    >
      {!isCreating && comments && comments.length > 0 && (
        <div className="comment-thread-comments">
          <FoldedComments<Comment_CommentFragment>
            comments={comments}
            limit={isExpanded ? EXPANDED_COMMENT_LIMIT : COMMENT_LIMIT}
            isExpanded={isExpanded}
            onUnfold={onResize}
            renderComment={(comment, idx) => (
              <Comment
                key={comment.id}
                viewState={viewState}
                first={idx === 0}
                initialShowingMore={initialShowingMore}
                onResize={onResize}
                comment={comment}
                threadId={threadId}
                post={post}
                onResolve={handleResolve}
                editing={editingCommentId === comment.id}
                onEdit={() => {
                  setEditingCommentId(comment.id);
                  onResize();
                }}
                onCancelEdit={() => {
                  setEditingCommentId(null);
                  onResize();
                }}
              />
            )}
          />
        </div>
      )}
      {isExpanded && (
        <div className="comment-thread-create">
          <CommentCreate
            postId={post.id}
            thread={thread}
            onChange={(value) => onInputChange?.(threadId, value)}
            onCreated={onCreated}
            onResize={onResize}
            initialContent={unsavedInputValue}
            disableAutoFocus={editingCommentId !== null}
          />
        </div>
      )}
    </div>
  );
};

export default CommentThreadFull;
